import {
  ITEMS,
  ItemKind,
  ItemRarity,
  LOOT_CATEGORIES,
  LOOT_CATEGORY_NAMES,
  LOOT_FILTER_ENABLED,
  LOOT_FILTER_MONEY,
  MAX_LOOT_FILTER,
  type ItemType,
  type LootCategory,
  type LootFilterPacket,
} from '@game/protocol';

import { collapsiblePanel } from './panel.ts';

/**
 * A tela do auto-loot (PRD §6).
 *
 * ## O que ela é
 *
 * Uma lista de tipos de item marcados. O que está marcado chega sozinho à mochila no instante
 * do abate; o resto continua dentro do corpo, à espera de um clique. O dinheiro tem
 * interruptor próprio porque é a única família que todo jogador quer em toda caçada — ver
 * `packets/loot-filter.ts` para a razão de ele não ser uma linha da lista.
 *
 * ## Por que ela deixou de ser uma janela
 *
 * Ela era a única automação do jogo e por isso abria sozinha, na tecla `L`. Com a auto-comida
 * e a auto-caçada, três janelas separadas dariam três teclas e três lugares para responder a
 * uma pergunta que é uma só — "o que está ligado agora?". Agora ela é uma **seção** de
 * `ui/automation.ts`, que é quem abre, fecha e posiciona; o que sobrou aqui é o conteúdo, e a
 * barra de título que recolhe a seção dentro do painel.
 *
 * ## Por que a lista começa nos itens marcados e não no catálogo inteiro
 *
 * São centenas de tipos, e rolar centenas de linhas para achar "escama de dragão" é a
 * interface que o jogador desiste de usar na segunda caçada. A busca é o caminho de entrada —
 * digita-se um pedaço do nome e marca-se —, e o que fica visível sem busca nenhuma é o que
 * **já** está marcado, que é a pergunta que a tela responde no dia a dia: "o que eu estou
 * pegando hoje?".
 *
 * Equipamento vestível, arma, poção e material aparecem na busca; corpo e contêiner não. Não é
 * arbitrário: um corpo não entra em mochila e um contêiner cheio de coisas de outra pessoa
 * recolhido por engano é o pior resultado possível de uma tela que existe para poupar atenção.
 *
 * ## Por que ela manda a lista inteira a cada clique
 *
 * O pacote sai por marcação, não por quadro, e uma lista completa é a única forma que não tem
 * a pergunta "e se o cliente e o servidor discordarem sobre o que havia antes". Ver o
 * comentário de `ClientOp.LootFilter`.
 */

export interface LootFilterHandlers {
  /** Manda a lista para o servidor. Chamado a cada marcação. */
  onChange(packet: LootFilterPacket): void;
}

/** Quantos resultados a busca mostra de uma vez. */
const SEARCH_LIMIT = 12;

export class LootFilterPanel {
  private readonly root: HTMLElement;
  private readonly handlers: LootFilterHandlers;

  private readonly search: HTMLInputElement;
  private readonly results: HTMLElement;
  private readonly chosen: HTMLElement;
  private readonly summary: HTMLElement;
  private readonly enabledBox: HTMLInputElement;
  private readonly moneyBox: HTMLInputElement;
  private readonly categoryRow: HTMLElement;
  private readonly categoryChips = new Map<LootCategory, HTMLButtonElement>();
  private readonly rarityPicker: HTMLSelectElement;

  private enabled = true;
  private money = true;
  private categories = 0;
  private minRarity: ItemRarity | null = null;
  private readonly typeIds = new Set<number>();

  constructor(root: HTMLElement, handlers: LootFilterHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.className = 'lootfilter';

    const header = collapsiblePanel(this.root, 'autoloot', 'Auto-loot');

    this.enabledBox = toggle('Recolher sozinho ao matar', () => {
      this.enabled = this.enabledBox.checked;
      this.commit();
    });
    this.moneyBox = toggle('Ouro e todo dinheiro', () => {
      this.money = this.moneyBox.checked;
      this.commit();
    });

    // As famílias, como fichas que ligam e desligam. Uma linha que embrulha, e não uma lista
    // vertical: são catorze, e catorze linhas empurrariam a busca e a lista marcada para fora
    // da primeira tela do painel — que é onde o jogador confere o que está pegando hoje.
    this.categoryRow = document.createElement('div');
    this.categoryRow.className = 'lootfilter-categories';

    for (const category of LOOT_CATEGORIES) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'lootfilter-chip';
      chip.textContent = LOOT_CATEGORY_NAMES[category];
      chip.addEventListener('click', () => {
        this.categories ^= 1 << category;
        this.commit();
      });

      this.categoryChips.set(category, chip);
      this.categoryRow.append(chip);
    }

    this.rarityPicker = document.createElement('select');
    this.rarityPicker.className = 'lootfilter-rarity';
    // "Desligado" primeiro e `Common` **ausente**: um piso em comum significa "traga tudo o
    // que cair", que enche a mochila de pele de rato e não é o que ninguém quer dizer ao
    // escolher uma raridade. Quem quer tudo de uma família marca a família.
    this.rarityPicker.append(option('', 'sem piso de raridade'));
    for (const rarity of RARITY_FLOORS) {
      this.rarityPicker.append(option(String(rarity), `${RARITY_NAMES[rarity]} ou melhor`));
    }
    this.rarityPicker.addEventListener('change', () => {
      const raw = this.rarityPicker.value;
      this.minRarity = raw === '' ? null : (Number(raw) as ItemRarity);
      this.commit();
    });
    // Pelo mesmo motivo do campo de busca logo abaixo, e por um caminho mais silencioso: um
    // `<select>` em foco consome as setas para navegar entre as opções, e as setas são como se
    // anda. Quem escolhe um piso de raridade e não clica fora fica preso sem nada na tela
    // sugerindo o porquê — não havia campo de texto piscando para acusar.
    this.rarityPicker.addEventListener('keydown', (event) => event.stopPropagation());

    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.className = 'lootfilter-search';
    this.search.placeholder = 'buscar item para marcar…';
    this.search.addEventListener('input', () => this.renderResults());
    // O Esc do campo não pode virar o Esc do jogo — lá ele desmarca o alvo e para a
    // caminhada, que é o oposto do que quem está digitando pediu.
    this.search.addEventListener('keydown', (event) => event.stopPropagation());

    this.results = document.createElement('div');
    this.results.className = 'lootfilter-results';

    this.chosen = document.createElement('div');
    this.chosen.className = 'lootfilter-chosen';

    this.summary = document.createElement('p');
    this.summary.className = 'lootfilter-summary';

    this.root.append(
      header.root,
      this.enabledBox.parentElement!,
      this.moneyBox.parentElement!,
      this.categoryRow,
      this.rarityPicker,
      this.search,
      this.results,
      this.chosen,
      this.summary,
    );

    this.render();
  }

  /**
   * Solta o teclado. Chamada pelo painel que a contém, quando ele fecha.
   *
   * O campo de busca **nunca** recebe foco sozinho, e a escolha custou um defeito para ser
   * aprendida: com ele em foco, `WASD` e as setas passam a digitar em vez de andar, e o
   * jogador que abriu a tela no meio de uma caçada fica **preso** até clicar fora. O clique
   * no campo continua a um gesto de distância, e ele não trava ninguém.
   *
   * Isto é a outra metade da mesma regra: quem clicou no campo e fechou o painel some da tela
   * com o teclado ainda preso a um elemento invisível — a mesma prisão, agora sem nada
   * visível para explicá-la.
   */
  releaseFocus(): void {
    this.search.blur();
  }

  /**
   * A lista que o servidor guarda. Chega uma vez, no `join`.
   *
   * Não devolve nada ao servidor: aplicar o que ele acabou de mandar e responder com a mesma
   * coisa seria um eco por login, e o primeiro que chegasse fora de ordem sobrescreveria uma
   * marcação recém-feita.
   */
  setFilter(packet: LootFilterPacket): void {
    this.enabled = (packet.flags & LOOT_FILTER_ENABLED) !== 0;
    this.money = (packet.flags & LOOT_FILTER_MONEY) !== 0;
    this.categories = packet.categories;
    this.minRarity = packet.minRarity;

    this.typeIds.clear();
    for (const typeId of packet.typeIds) this.typeIds.add(typeId);

    this.render();
  }

  private commit(): void {
    this.render();
    this.handlers.onChange({
      flags: (this.enabled ? LOOT_FILTER_ENABLED : 0) | (this.money ? LOOT_FILTER_MONEY : 0),
      categories: this.categories,
      minRarity: this.minRarity,
      typeIds: [...this.typeIds],
    });
  }

  private render(): void {
    this.enabledBox.checked = this.enabled;
    this.moneyBox.checked = this.money;
    this.rarityPicker.value = this.minRarity === null ? '' : String(this.minRarity);

    for (const [category, chip] of this.categoryChips) {
      chip.classList.toggle('marked', (this.categories & (1 << category)) !== 0);
    }

    // Desligado continua mostrando a lista, apagada: ela não foi perdida, está em pausa, e
    // uma tela que esvazia ao desligar ensina o jogador a não desligar.
    this.root.classList.toggle('lootfilter-off', !this.enabled);

    this.renderChosen();
    this.renderResults();
    this.summary.textContent = this.describe();
  }

  /**
   * Uma frase dizendo o que vai vir sozinho.
   *
   * Existe porque as três réguas somadas não se leem de relance: catorze fichas, um piso de
   * raridade e uma lista de nomes são três lugares para olhar, e a pergunta que o jogador faz
   * ao abrir a tela é uma só. A frase é o lugar onde ela é respondida — e é também o único
   * lugar onde "nada vem sozinho" pode aparecer, que é o estado em que a tela parece ligada e
   * não faz nada.
   */
  private describe(): string {
    const parts: string[] = [];

    if (this.money) parts.push('dinheiro');

    const families = [...this.categoryChips.keys()].filter(
      (category) => (this.categories & (1 << category)) !== 0,
    );
    if (families.length > 0) {
      parts.push(
        families.length <= 3
          ? families.map((c) => LOOT_CATEGORY_NAMES[c].toLowerCase()).join(', ')
          : `${families.length} famílias`,
      );
    }

    if (this.minRarity !== null) {
      parts.push(`${RARITY_NAMES[this.minRarity].toLowerCase()} ou melhor`);
    }

    if (this.typeIds.size > 0) {
      parts.push(
        this.typeIds.size === 1 ? '1 item marcado' : `${this.typeIds.size} itens marcados`,
      );
    }

    if (parts.length === 0) return 'Nada marcado — nada vem sozinho.';

    const limite =
      this.typeIds.size >= MAX_LOOT_FILTER ? ` (limite de ${MAX_LOOT_FILTER} itens)` : '';
    return `Vem sozinho: ${parts.join(' · ')}.${limite}`;
  }

  private renderChosen(): void {
    this.chosen.replaceChildren();

    const marked = [...this.typeIds]
      .map((typeId) => ITEMS[typeId])
      .filter((type): type is ItemType => type !== undefined)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

    for (const type of marked) {
      this.chosen.append(this.row(type, true));
    }
  }

  private renderResults(): void {
    this.results.replaceChildren();

    const query = this.search.value.trim().toLowerCase();
    if (query.length === 0) return;

    const found = Object.values(ITEMS)
      .filter((type) => lootable(type) && !this.typeIds.has(type.id))
      .filter((type) => type.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      .slice(0, SEARCH_LIMIT);

    if (found.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'lootfilter-summary';
      empty.textContent = 'Nenhum item com esse nome.';
      this.results.append(empty);
      return;
    }

    for (const type of found) this.results.append(this.row(type, false));
  }

  /** Uma linha da lista. Marcada tem o botão de tirar; encontrada, o de pôr. */
  private row(type: ItemType, marked: boolean): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = marked ? 'lootfilter-row marked' : 'lootfilter-row';

    const name = document.createElement('span');
    name.className = 'lootfilter-row-name';
    name.textContent = type.name;

    const action = document.createElement('span');
    action.className = 'lootfilter-row-action';
    action.textContent = marked ? '✕' : '+';

    row.append(name, action);
    row.addEventListener('click', () => {
      if (marked) {
        this.typeIds.delete(type.id);
      } else {
        // O teto existe no protocolo e a recusa precisa ser dita aqui: um clique que não faz
        // nada e não explica é indistinguível de um clique que a tela não entendeu.
        if (this.typeIds.size >= MAX_LOOT_FILTER) {
          this.summary.textContent = `A lista já tem ${MAX_LOOT_FILTER} itens — tire um para pôr outro.`;
          return;
        }
        this.typeIds.add(type.id);
        this.search.value = '';
      }
      this.commit();
    });

    return row;
  }
}

/**
 * Este tipo pode ser marcado?
 *
 * Corpo e contêiner ficam de fora — ver o comentário da classe. O resto do catálogo entra,
 * inclusive o que ninguém marcaria: decidir por valor ou raridade esconderia justamente o item
 * que a pessoa quer e o jogo não considera valioso, que é o caso em que uma lista existe.
 */
function lootable(type: ItemType): boolean {
  return type.kind !== ItemKind.Corpse && type.kind !== ItemKind.Container;
}

/**
 * Os pisos de raridade que a tela oferece — `Common` fica de fora de propósito.
 *
 * Escolher "comum ou melhor" é escolher tudo, e tudo é o que a mochila não aguenta: numa
 * caçada de uma hora isso são cinquenta peles de rato. Quem quer uma família inteira marca a
 * família, que é a régua feita para isso.
 */
const RARITY_FLOORS: readonly ItemRarity[] = [
  ItemRarity.Uncommon,
  ItemRarity.Rare,
  ItemRarity.Epic,
  ItemRarity.Legendary,
  ItemRarity.Celestial,
];

const RARITY_NAMES: Readonly<Record<ItemRarity, string>> = {
  [ItemRarity.Common]: 'Comum',
  [ItemRarity.Uncommon]: 'Incomum',
  [ItemRarity.Rare]: 'Rara',
  [ItemRarity.Epic]: 'Épica',
  [ItemRarity.Legendary]: 'Lendária',
  [ItemRarity.Celestial]: 'Celestial',
};

function option(value: string, label: string): HTMLOptionElement {
  const element = document.createElement('option');
  element.value = value;
  element.textContent = label;
  return element;
}

function toggle(label: string, onChange: () => void): HTMLInputElement {
  const line = document.createElement('label');
  line.className = 'lootfilter-toggle';

  const box = document.createElement('input');
  box.type = 'checkbox';
  box.addEventListener('change', onChange);

  const text = document.createElement('span');
  text.textContent = label;

  line.append(box, text);
  return box;
}
